/* eslint-disable arrow-parens, max-len */
import React, { useState, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Spinner } from 'reactstrap'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useToasts } from 'react-toast-notifications'
import userApi from '_api/user'
import { getUserInfo } from '_utils/localData'
import moment from 'moment'
import DatePicker from './DatePicker'

const AddAddressDeliveryModal = ({ isOpen, toggle, onAddressAdded, phoneNumber, userAddresses = [], eventDetail }) => {
  const { addToast } = useToasts()
  const userInfo = getUserInfo()
  const [isLoading, setIsLoading] = useState(false)

  const hasNoAddresses = !userAddresses || userAddresses.length === 0
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)

  const pickupLimitDate = eventDetail?.pickupTimestamp
    ? new Date(eventDetail.pickupTimestamp * 1000)
    : null

  const parseDeliveryDate = (val) => {
    if (!val) return null
    if (val instanceof Date && !isNaN(val.getTime())) return val
    const m = moment(val, 'DD-MM-YYYY', true)
    if (m.isValid()) return m.toDate()
    const iso = new Date(val)
    return !isNaN(iso.getTime()) ? iso : null
  }

  const validationSchema = Yup.object().shape({
    deliveryDate: Yup.date()
    .required('Delivery date is required')
    .test('not-past', 'Delivery date cannot be in the past', (value) => {
      if (!value) return false
      const v = new Date(value)
      v.setHours(0, 0, 0, 0)
      return v.getTime() >= todayStart.getTime()
    })
    .test('before-pickup', 'Delivery date must be before pickup date', (value) => {
      if (!value) return false
      if (!pickupLimitDate) return true
      const v = new Date(value)
      return v.getTime() < pickupLimitDate.getTime()
    }),
    name: Yup.string()
      .required('Address name is required')
      .min(3, 'Address name must be at least 3 characters'),
    address: Yup.string()
      .required('Address is required')
      .min(3, 'Address must be at least 3 characters'),
    postalCode: Yup.string()
      .required('Postal code is required')
      .min(3, 'Postal code must be at least 3 characters'),
    country: Yup.string()
      .required('Country is required'),
    phone: Yup.string()
      .required('Phone number is required')
      .min(8, 'Phone number must be at least 8 characters'),
    consignee: Yup.string()
      .required('Consignee is required')
      .min(3, 'Consignee must be at least 3 characters'),
    isDefault: Yup.boolean().default(false)
  })

    const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: 'Home',
      consignee: userInfo?.name || '',
      address: '',
      postalCode: '',
      country: 'Singapore',
      phone: phoneNumber || userInfo?.phone || '',
      isDefault: hasNoAddresses,
      deliveryDate: todayStart
    }
  })

  const formValues = watch() // eslint-disable-line
  useEffect(() => {
    if (isOpen) {
      const initial = parseDeliveryDate(eventDetail?.deliveryDate) || todayStart
      setValue('deliveryDate', initial)
    }
  }, [isOpen, eventDetail, setValue])

  useEffect(() => {
    const noAddresses = !userAddresses || userAddresses.length === 0
    setValue('isDefault', noAddresses)
  }, [userAddresses, setValue])

  const onSubmit = async (data) => {
    setIsLoading(true)

    try {
      const newAddressObj = {
        deliveryDate: moment(data.deliveryDate).format('DD-MM-YYYY'),
        name: data.name,
        consignee: data.consignee,
        postalCode: data.postalCode,
        address: {
          street: data.address
        },
        country: data.country,
        phone: data.phone,
        isDefault: data.isDefault,
        id: `temp_${Date.now()}`
      }

      reset({
        name: 'Home',
        consignee: userInfo?.name || '',
        address: '',
        postalCode: '',
        country: 'Singapore',
        phone: userInfo?.phone || '',
        isDefault: hasNoAddresses
      })

      toggle()

      if (onAddressAdded) {
        onAddressAdded(newAddressObj)
      }

      userApi.createUserAddress({
        name: data.name,
        consignee: data.consignee,
        postalCode: data.postalCode,
        address: {
          street: data.address
        },
        country: data.country,
        phone: data.phone,
        isDefault: data.isDefault
      }).then(() => {
        addToast('Address added successfully!', { appearance: 'success', autoDismiss: true })
      }).catch((error) => {
        console.error('Failed to save address to server:', error)
      })
    } catch (error) {
      addToast('Failed to process address', { appearance: 'error', autoDismiss: true })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    const noAddresses = !userAddresses || userAddresses.length === 0
    reset({
      name: 'Home',
      consignee: userInfo?.name || '',
      address: '',
      postalCode: '',
      country: 'Singapore',
      phone: userInfo?.phone || '',
      isDefault: noAddresses
    })
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered style={{ maxWidth: '498px' }}>
      <ModalHeader toggle={handleClose}>Delivery Details</ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <FormGroup>
            <Label for="deliveryDate" style={{ fontSize: '16px' }}>
              Delivery Date<span style={{ color: 'red' }}>*</span>
            </Label>
            <div className="have-icon">
              <Controller
                name="deliveryDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    onChange={(date) => field.onChange(date)}
                    value={field.value}
                    minDate={todayStart}
                    maxDate={pickupLimitDate ? new Date(pickupLimitDate.getTime() - 1) : undefined}
                  />
                )}
              />
            </div>
            {errors.deliveryDate && <div className="invalid-feedback d-block">{errors.deliveryDate.message}</div>}
          </FormGroup>
          <FormGroup>
            <Label for="consignee" style={{ fontSize: '16px' }}>
              Consignee <span style={{ color: 'red' }}>*</span>
            </Label>
            <Controller
              name="consignee"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="text"
                  id="consignee"
                  className="small-placeholder"
                  placeholder="Recipient name"
                  invalid={!!fieldState.error}
                />
              )}
            />
            {errors.consignee && <div className="invalid-feedback">{errors.consignee.message}</div>}
          </FormGroup>
          <FormGroup>
            <Label for="name" style={{ fontSize: '16px' }}>
              Address Label (e.g. Home, Office)<span style={{ color: 'red' }}>*</span>
            </Label>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="text"
                  id="name"
                  className="small-placeholder"
                  placeholder="e.g. Home, Office, etc."
                  invalid={!!fieldState.error}
                />
              )}
            />
            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="address" style={{ fontSize: '16px' }}>
              Address <span style={{ color: 'red' }}>*</span>
            </Label>
            <Controller
              name="address"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="text"
                  id="address"
                  className="small-placeholder"
                  placeholder="Enter your full address"
                  invalid={!!fieldState.error}
                />
              )}
            />
            {errors.address && <div className="invalid-feedback">{errors.address.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="postalCode" style={{ fontSize: '16px' }}>
              Postal Code <span style={{ color: 'red' }}>*</span>
            </Label>
            <Controller
              name="postalCode"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="text"
                  id="postalCode"
                  className="small-placeholder"
                  placeholder="Enter postal code"
                  invalid={!!fieldState.error}
                />
              )}
            />
            {errors.postalCode && <div className="invalid-feedback">{errors.postalCode.message}</div>}
          </FormGroup>

          <FormGroup>
              <Label for="country" style={{ fontSize: '16px' }}>
                Country <span style={{ color: 'red' }}>*</span>
              </Label>
              <div style={{ position: 'relative' }}>
                <Controller
                  name="country"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      type="select"
                      id="country"
                      className="small-placeholder custom-country-select"
                      invalid={!!fieldState.error}
                    >
                      <option value="Singapore">Singapore</option>
                    </Input>
                  )}
                />
                <div
                  style={{
                    position: 'absolute',
                    right: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    fontSize: '12px',
                    color: '#6c757d'
                  }}
                >
                  ▾
                </div>
              </div>
              {errors.country && <div className="invalid-feedback">{errors.country.message}</div>}
            </FormGroup>

          <FormGroup>
            <Label for="phone" style={{ fontSize: '16px' }}>
              Phone Number <span style={{ color: 'red' }}>*</span>
            </Label>
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="tel"
                  id="phone"
                  className="small-placeholder"
                  placeholder="Enter phone number"
                  invalid={!!fieldState.error}
                />
              )}
            />
            {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
          </FormGroup>

          <FormGroup check>
            <Controller
              name="isDefault"
              control={control}
              render={({ field }) => (
                <Label check style={{ fontSize: '16px' }}>
                  <Input
                    type="checkbox"
                    {...field}
                    checked={!!field.value}
                    disabled={hasNoAddresses}
                  />
                  Set as default address
                  {hasNoAddresses && (
                    <small style={{ color: '#6c757d', marginLeft: '8px', display: 'block' }}>
                      (automatically set as default for first address)
                    </small>
                  )}
                </Label>
              )}
            />
          </FormGroup>
          {/* debug */}
          {/* <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h5>Form Values:</h5>
            <pre>{JSON.stringify(formValues, null, 2)}</pre>
          </div> */}
        </ModalBody>

        <ModalFooter>
          <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
            <Button color="light" onClick={handleClose} disabled={isLoading} style={{ border: '1px solid #07C3C3' }}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isLoading} style={{ borderRadius: '25px' }}>
              {isLoading ? <Spinner size="sm" /> : 'Submit'}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default AddAddressDeliveryModal
